import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { Box, Chip, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { useMultiTableData } from "@accredo/fe-shared/components/tables/hooks/useMultiTableData.jsx";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";
import { Button } from "@accredo/fe-shared/theme/components";

import SearchField from "./SearchField.jsx";
import ContractCardList from "./ContractCardList.jsx";

/**
 * The portal's own Vertrag list, built on the same
 * DataTable/useMultiTableData the Schadenportal's ContractsTable.jsx uses,
 * but with portal-specific columns and always the widened range - unlike the
 * Schadenportal, the Kundenportal's archive has no age limit, so every
 * request this table makes carries `range=all`.
 *
 * A table of its own rather than portal branches on ContractsTable.jsx: the
 * two lists show different columns for a different audience.
 *
 * `title`: undefined by default, so /vertraege keeps its plain look (its own
 * pager already shows a running total). A caller that wants a card header
 * with a total count - the dashboard - passes one; the count is appended
 * only once the real total has loaded, never as a placeholder 0.
 */
export default function PortalContractsTable({ title } = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down("md"));

  // Both live in the URL, like BankAccountTable's review_state, so
  // arriving with a Sparte filter (e.g. from the dashboard) can be deep-linked.
  // activeSparte is the catalog Sparte's id (see SparteSummaryCard), not its
  // label - resolved back to a label below, from the filtered rows
  // themselves, purely for the "Gefiltert nach..." banner text.
  const activeStatus = searchParams.get("status") || "all";
  const activeSparte = searchParams.get("sparte");
  // Not shown when embedded with a title (the dashboard's own contract
  // table) - that page has its own cross-entity GlobalSearch instead, so a
  // second, table-local search field here would be redundant.
  const activeSearch = title ? "" : searchParams.get("search") || "";

  const tableKey = "contracts";

  const sharedFilters = useMemo(
    () => ({
      ...(activeSparte && { sparte: activeSparte }),
      ...(activeSearch && { _searchword: activeSearch }),
    }),
    [activeSparte, activeSearch]
  );

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Verträge",
        route: "/data/vertrags",
        pageSize: 10,
        storageKey: "kp_table_contracts",
        extraParams: {
          range: "all",
          include: ["objekts", "display_sparten", "letzter_nachtrag"],
          sort: [{ field: "ablauf", order: "desc" }],
          filters: {
            ...sharedFilters,
            ...(activeStatus !== "all" && { status: activeStatus }),
          },
        },
      },
      // Count-only requests (limit 1) for the Aktiv/Archiviert tab badges -
      // "Alle" is their sum, so it never needs a third round trip. Their own
      // storageKey keeps them from ever picking up the main table's stored
      // page size.
      {
        key: "countActive",
        label: "Verträge Aktiv",
        route: "/data/vertrags",
        pageSize: 1,
        storageKey: "kp_table_contracts_count_active",
        extraParams: {
          range: "all",
          filters: { ...sharedFilters, status: "active" },
        },
      },
      {
        key: "countArchived",
        label: "Verträge Archiviert",
        route: "/data/vertrags",
        pageSize: 1,
        storageKey: "kp_table_contracts_count_archived",
        extraParams: {
          range: "all",
          filters: { ...sharedFilters, status: "archived" },
        },
      },
    ],
    [activeStatus, sharedFilters]
  );

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  // The filtered rows all carry the active Sparte (that's what the filter
  // guarantees), so its label can be read back off any one of them rather
  // than needing a second lookup just to resolve an id to a label.
  const activeSparteLabel = activeSparte
    ? rows
        .flatMap((row) => row.sparten || [])
        .find((sparte) => String(sparte.sparte_id) === activeSparte)?.sparte
    : null;

  // Initial load and every status/Sparte change go through the hook's own
  // fetch path - shouldFetch must be explicit, since pageIndex is already 0
  // and { pageIndex: 0 } alone would be a no-op that never fetches.
  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus, activeSparte, activeSearch]);

  // The two count-only tables depend on the Sparte and search filters, not
  // the active status tab - refetching them on every tab click would be a
  // pointless extra round trip, since Aktiv/Archiviert's own counts never
  // change when the user is just switching which tab is shown.
  useEffect(() => {
    updateTableState("countActive", { pageIndex: 0, shouldFetch: true });
    updateTableState("countArchived", { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSparte, activeSearch]);

  const handleTabChange = (value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("status");
        } else {
          next.set("status", value);
        }
        return next;
      },
      { replace: true }
    );
  };

  const clearSparteFilter = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("sparte");
        next.delete("status");
        return next;
      },
      { replace: true }
    );
  };

  // Typing stays instant (local state); the URL - and the fetch its change
  // triggers above - only updates once typing pauses.
  const [searchInput, setSearchInput] = useState(activeSearch);
  const debouncedSearchInput = useDebounce(searchInput, 300);
  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearchInput) {
          next.set("search", debouncedSearchInput);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const handleChangePage = (nextOrUpdater) => {
    const base = {
      pageIndex: tableState.pageIndex ?? 0,
      pageSize: tableState.pageSize ?? 10,
    };
    const next =
      typeof nextOrUpdater === "function" ? nextOrUpdater(base) : nextOrUpdater;
    if (!next) {
      return;
    }
    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  const handleRowClick = (row) => navigate(`/vertraege/${row.id}`);

  const countActive = tableStates.countActive?.totalRows ?? 0;
  const countArchived = tableStates.countArchived?.totalRows ?? 0;

  const filterTabs = [
    { key: "all", label: "Alle", count: countActive + countArchived },
    { key: "active", label: "Aktiv", count: countActive },
    { key: "archived", label: "Archiviert", count: countArchived },
  ].map((tab) => ({ ...tab, onClick: handleTabChange }));

  const columns = [
    {
      id: "objekt_vertrag",
      header: "Objekt / Vertrag",
      accessorKey: "vertragsnummer",
      // The cell shows the single Objekt's name or falls back to the
      // Vertragsnummer (see PortalContractsTable's header comment) - neither
      // is a single sortable server field, and "objekt_vertrag" is not in
      // ContractRepository's allowedSorts(), so sorting this column would
      // otherwise send a `sort=` value the API rejects with 400.
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const objekte = row.original.objekts || [];
        const primary =
          objekte.length === 1
            ? objekte[0].bezeichnung
            : row.original.vertragsnummer;

        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {primary}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.vertragsart}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "vertragsnummer",
      header: "Vertragsnummer",
    },
    {
      id: "sparte",
      header: "Sparte",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.original.sparten || []).map((sparte) => (
            <Chip key={sparte.id} size="small" label={sparte.sparte} />
          ))}
        </Stack>
      ),
    },
    {
      id: "letzter_nachtrag",
      header: "Letzter Nachtrag",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        row.original.letzter_nachtrag
          ? getDatum(row.original.letzter_nachtrag)
          : "–",
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Chip
          size="small"
          label={row.original.is_active ? "aktiv" : "archiviert"}
          variant={
            row.original.is_active
              ? "contract-status_aktiv"
              : "contract-status_archiviert"
          }
        />
      ),
    },
  ];

  return (
    <>
      {!title && (
        <SearchField
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          isLoading={tableState.isLoading}
        />
      )}
      {activeSparte && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2">
            Gefiltert nach Sparte:{" "}
            <strong>{activeSparteLabel ?? activeSparte}</strong>
          </Typography>
          <Button variant="tertiary" size="small" onClick={clearSparteFilter}>
            Filter zurücksetzen
          </Button>
        </Box>
      )}

      {is_mobile ? (
        <ContractCardList
          rows={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          title={
            title
              ? title +
                (tableState.hasLoaded ? ` (${tableState.totalRows})` : "")
              : undefined
          }
          onChangePage={handleChangePage}
          filterTabs={filterTabs}
          activeFilterTab={activeStatus}
          onRowClick={handleRowClick}
        />
      ) : (
        <DataTable
          testId="portal-contracts-table"
          columns={columns}
          data={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          title={
            title
              ? title +
                (tableState.hasLoaded ? ` (${tableState.totalRows})` : "")
              : undefined
          }
          onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
          onChangePage={handleChangePage}
          storageKey="kp_table_contracts"
          enableSorting
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableColumnHiding={false}
          filterTabs={filterTabs}
          activeFilterTab={activeStatus}
          onRowClick={handleRowClick}
        />
      )}
    </>
  );
}
